/**
 * Canvas UI 2.0 — Variants / Archived tab chips for the variants panel.
 *
 * Replaces the RFC-1146 Like/Neutral/Dislike filter row. Those were three
 * INDEPENDENT toggles (any combination legal, including all-off → empty grid);
 * this is a genuine two-way segmented control, so exactly one tab is always
 * active and the "user filtered everything away" state is gone.
 *
 * Defaults to Variants every time a node's variant view opens — session-only
 * React state, deliberately not persisted (same rule the filter chips had).
 *
 * The counts come from the live marker map, so they move the instant a star is
 * clicked — as does the tile itself, which is now partitioned live rather than
 * against a frozen open-time snapshot (see useVariantMarkers).
 */

import { memo, useCallback } from 'react'
import { FolderCheck, FolderX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { VARIANT_TABS, type VariantTab } from './variantKey'

export interface VariantTabChipsProps {
  /** Currently active tab. */
  tab: VariantTab
  /** Switch tab. The parent resyncs the marker snapshot. */
  onSelect: (tab: VariantTab) => void
  /** Per-tab totals, shown as counts on each chip. */
  counts: Record<VariantTab, number>
}

/** Fixed height in px the chip row occupies, including its bottom margin.
 *
 * Exported so `computeVariantPanelLayout` / `getVideoVariantsHeight` can
 * reserve exactly this much: the panel height and the rendered grid diverging
 * is a known prior bug class in these files (post 1533007285123236). */
export const VARIANT_TAB_ROW_HEIGHT = 36

const TAB_ICON = {
  variants: FolderCheck,
  archived: FolderX,
}

const VariantTabChips = memo(function VariantTabChips({
  tab,
  onSelect,
  counts,
}: VariantTabChipsProps) {
  const { t } = useTranslation('canvas')

  const handleSelect = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, next: VariantTab) => {
      event.stopPropagation()
      onSelect(next)
    },
    [onSelect],
  )

  return (
    <div
      className="flex items-center gap-1.5 mb-2"
      style={{ height: VARIANT_TAB_ROW_HEIGHT - 8 }}
      role="tablist"
      aria-label={t('variantMarker.tabListLabel')}
      data-testid="variant-tab-chips"
    >
      {VARIANT_TABS.map((key) => {
        const Icon = TAB_ICON[key]
        const active = tab === key
        const label = t(`variantMarker.tab.${key}`)
        return (
          <button
            key={key}
            type="button"
            role="tab"
            onClick={(event) => handleSelect(event, key)}
            title={label}
            aria-label={label}
            aria-selected={active}
            data-testid={`variant-tab-chip-${key}`}
            data-active={active}
            className={
              // Plain template literal (not cn/twMerge) — see
              // VariantMarkerButton for why twMerge is avoided on these.
              `h-6 px-2 rounded-full border text-xxs font-medium flex items-center gap-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                active
                  ? 'border-primary/60 bg-primary/15 text-foreground'
                  : 'border-border-subtle bg-surface-1 text-text-muted hover:border-border'
              }`
            }
          >
            <Icon className="w-3 h-3" />
            <span>{label}</span>
            <span className="opacity-60">{counts[key]}</span>
          </button>
        )
      })}
    </div>
  )
})

VariantTabChips.displayName = 'VariantTabChips'

export { VariantTabChips }
